import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import Response_Processing from "../../layout/Others/Response_Processing";
import { getItemToken } from "../../Utils/localStorageUtils";
import AdminBackNavigation from "../../components/CommonComponents/AdminBackNavigation";
import AdminSaveHeader from "../../components/CommonComponents/AdminSaveHeader";
import FormContainer from "../../components/CommonComponents/FormContainer";
import LeftWrapper from "../../components/CommonComponents/LeftWrapper";
import RightWrapper from "../../components/CommonComponents/RightWrapper";
import Wrapper from "../../components/CommonComponents/Wrapper";
import MessageHeading from "../../components/CommonComponents/MessageHeading";
import MessageLabel from "../../components/CommonComponents/MessageLabel";
import MessageSubHeading from "../../components/CommonComponents/MessageSubHeading";
import AdminCustomInput from "../../components/CommonComponents/AdminCustomInput";
import AdminCustomArea from "../../components/CommonComponents/AdminCustomArea";
import AdminCustomSelect from "../../components/CommonComponents/AdminCustomSelect";
import AdminSwitch from "../../components/CommonComponents/AdminSwitch";
import AdminCustomButton from "../../components/CommonComponents/AdminCustomButton";
import MessageBox from "../../components/CommonComponents/MessageBox";

const AddPPVPurchasePlans = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const baseURL = process.env.REACT_APP_Baseurl;
  const headers = getItemToken('access_token');

  const createApi = `${baseURL}/admin/ppv-purchase-plans/store`;
  const editApi = `${baseURL}/admin/ppv-purchase-plans/edit`;
  const updateApi = `${baseURL}/admin/ppv-purchase-plans/update`;

  const combinedRef = useRef({
    plan_id: null,
    title: null,
    price: null,
  });

  const [formData, setFormData] = useState({
    plan_id: "",
    title: "",
    description: "",
    price: "",
    content_types: [],
    discount_percentage: "",
    is_active: 1,
  });

  const [formDataError, setFormDataError] = useState({
    plan_id: "",
    title: "",
    price: "",
    content_types: "",
  });

  const [defaultOption, setDefaultOption] = useState({
    contentTypes: []
  });

  const [showOverlay, setShowOverlay] = useState(false);

  const multiSelectLabel = (array, valueKey, labelKey) => {
    return Array.isArray(array)
      ? array.map((item) => ({
        value: String(item[valueKey]),
        label: item[labelKey]
      }))
      : [];
  };

  const handleMultiSelect = (selectedOptions, actionMeta) => {
    setFormData((prev) => ({
      ...prev,
      [actionMeta.name]: selectedOptions
    }));

    // Clear error when user makes selection
    if (formDataError[actionMeta.name]) {
      setFormDataError((prev) => ({
        ...prev,
        [actionMeta.name]: '',
      }));
    }
  };

  const formValidation = () => {
    let formIsValid = true;
    const errors = {};

    if (!formData?.plan_id || formData.plan_id.trim().length === 0) {
      errors.plan_id = "Plan ID is required";
      formIsValid = false;
    }

    if (!formData?.title || formData.title.trim().length === 0) {
      errors.title = "Title is required";
      formIsValid = false;
    }

    if (!formData?.price || formData.price <= 0) {
      errors.price = "Price must be greater than 0";
      formIsValid = false;
    }

    if (!formData?.content_types || formData.content_types.length === 0) {
      errors.content_types = "Content Types are required";
      formIsValid = false;
    }

    setFormDataError(errors);
    return formIsValid;
  };

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked ? 1 : 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing
    if (formDataError[name]) {
      setFormDataError((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await axios.get(`${baseURL}/admin/ppv-purchase-plans/create`, { headers });
      const result = response?.data?.data;

      setDefaultOption({
        contentTypes: multiSelectLabel(result?.contentTypes, 'value', 'name') || []
      });
    } catch (error) {
      console.error('Error fetching form options:', error);
      toast.error('Error loading form options');
    }
  };

  const fetchEditData = async () => {
    try {
      setShowOverlay(true);


      const response = await axios.get(`${editApi}/${id}`, { headers });


      const result = response?.data?.data;

      if (result?.ppv) {
        const ppv = result.ppv;
        // Parse content_types from DB (comma-separated format)
        let contentTypesArray = [];
        const rawContentTypes = ppv.content_types;

        if (typeof rawContentTypes === 'string' && rawContentTypes.trim()) {
          // Comma-separated: "4,5,2"
          contentTypesArray = rawContentTypes.split(',').map(s => s.trim()).filter(Boolean);
        }

        // Build all options first
        const allOptions = multiSelectLabel(result?.contentTypes, 'value', 'name') || [];

        // Find matching selected options based on DB values
        const selectedOptions = allOptions.filter(option =>
          contentTypesArray.includes(String(option.value))
        );

        const formDataToSet = {
          plan_id: ppv.plan_id || "",
          title: ppv.title || "",
          description: ppv.description || "",
          price: ppv.price || "",
          content_types: selectedOptions,
          discount_percentage: ppv.discount_percentage || "",
          is_active: ppv.is_active || 1,
        };

        setFormData(formDataToSet);

        setDefaultOption({
          contentTypes: allOptions
        });
      } else {
        console.log('No PPV data found in result:', result);
        toast.error('PPV record not found');
      }
    } catch (error) {
      console.error('Error fetching edit data:', error);
      toast.error('Error loading PPV record');
    } finally {
      setShowOverlay(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formValidation()) {
      return;
    }

    setShowOverlay(true);

    try {
      const url = isEdit ? `${updateApi}/${id}` : createApi;

      // Prepare form data with content_types as array of values
      const submitData = {
        ...formData,
        content_types: Array.isArray(formData.content_types)
          ? formData.content_types.map(option => option.value).filter(value => value !== null && value !== undefined)
          : []
      };

      // Remove empty discount_percentage to avoid validation error
      if (submitData.discount_percentage === '' || submitData.discount_percentage === null || submitData.discount_percentage === undefined) {
        delete submitData.discount_percentage;
      }

      const response = await axios.post(url, submitData, { headers });

      if (response.data.status) {
        toast.success(`PPV Purchase Plan ${isEdit ? 'updated' : 'created'} successfully`);
        navigate('/admin/ppv-purchase-plans');
      } else {
        toast.error(response.data.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error saving PPV record:', error);
      if (error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach(err => {
          serverErrors[err.param] = err.msg;
        });
        setFormDataError(serverErrors);
      }
      toast.error(error.response?.data?.message || `Error ${isEdit ? 'updating' : 'creating'} PPV record`);
    } finally {
      setShowOverlay(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchFormOptions();
      if (isEdit) {
        await fetchEditData();
      }
    };
    init();
  }, [id]);

  return (
    <>
      {showOverlay && <Response_Processing />}
      <AdminBackNavigation path={'/admin/ppv-purchase-plans'} text="Back" />
      <AdminSaveHeader
        heading={isEdit ? 'Edit PPV Purchase Plan' : 'Add PPV Purchase Plan'}
        saveText={isEdit ? 'Update' : 'Save'}
        saveMethod={handleSubmit}
        type="submit"
      />
      <form onSubmit={handleSubmit}>
        <FormContainer>
          <LeftWrapper>
            <Wrapper>
              <MessageHeading text='Basic Information' />

              <MessageLabel text='Title' className="mandatorySimple" />
              <MessageSubHeading text="Enter the title for the PPV purchase plan" />
              <AdminCustomInput
                type="text"
                name="title"
                value={formData?.title}
                onChange={handleInput}
                placeHolder="Enter plan title"
                ref={(el) => (combinedRef.current.title = el)}
                error={formDataError?.title}
              />

              <MessageLabel text='Plan ID' className="mandatorySimple" />
              <MessageSubHeading text="Enter a unique identifier for the PPV plan" />
              <AdminCustomInput
                type="text"
                name="plan_id"
                value={formData?.plan_id}
                onChange={handleInput}
                placeHolder="Enter plan ID"
                ref={(el) => (combinedRef.current.plan_id = el)}
                error={formDataError?.plan_id}
                maxLength={50}
              />

              <MessageLabel text='Price' className="mandatorySimple" />
              <MessageSubHeading text="Enter the price for the PPV plan" />
              <AdminCustomInput
                type="number"
                name="price"
                value={formData?.price}
                onChange={handleInput}
                placeHolder="Enter price"
                min="0.01"
                step="0.01"
                ref={(el) => (combinedRef.current.price = el)}
                error={formDataError?.price}
              />

              <MessageLabel text="Discount Percentage" />
              <MessageSubHeading text="Enter discount percentage (0-100)" />
              <AdminCustomInput
                type="number"
                name="discount_percentage"
                value={formData?.discount_percentage}
                onChange={handleInput}
                placeHolder="Enter discount percentage"
                min="0"
                max="100"
                step="0.01"
              />
            </Wrapper>
          </LeftWrapper>

          <RightWrapper>

            <Wrapper>
              <MessageHeading text="Content Details" />

              <MessageLabel text="Content Types" />
              <MessageSubHeading text="Select the types of content this plan applies to (multiple allowed)" />
              <Select
                options={defaultOption?.contentTypes}
                isMulti
                name="content_types"
                onChange={handleMultiSelect}
                value={formData?.content_types}
                className="custom-select-multi custom-placeholder theme-text-color mb-3"
                placeholder="Select Content Types"
              />
              {formDataError?.content_types && (<MessageBox text={`${formDataError?.content_types}`} classname='errorred mb-2 d-block' />)}

              <MessageLabel text='Description' />
              <MessageSubHeading text="Enter detailed description of the PPV plan" />
              <AdminCustomArea
                name="description"
                value={formData?.description}
                onChange={handleInput}
                placeHolder="Enter detailed description"
                rows={4}
                maxLength={1000}
              />
            </Wrapper>

            <Wrapper>
              <MessageHeading text='Settings' />
              <div className="d-flex justify-content-between mb-2">
                <div className="col-6 col-md-6 pe-2">
                  <MessageLabel text='Status' />
                  <MessageSubHeading text="Enable or disable this plan" />
                </div>
                <div className="col-6 col-md-6 ps-2 text-end">
                  <AdminSwitch
                    name="is_active"
                    checked={formData?.is_active === 1}
                    onChange={handleInput}
                  />
                </div>
              </div>
            </Wrapper>
          </RightWrapper>
        </FormContainer>
        
        <div className="d-flex justify-content-end mt-4 mb-4">
          <AdminCustomButton 
            saveMethod={handleSubmit} 
            type="submit" 
            saveText={isEdit ? "Update Plan" : "Save Plan"} 
            loading={showOverlay} 
          />
        </div>
      </form>
    </>
  );
};

export default AddPPVPurchasePlans;
